import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  FiSearch,
  FiBook,
  FiHelpCircle,
  FiVideo,
  FiFileText,
  FiArrowRight,
  FiUsers,
  FiDollarSign,
  FiTool,
} from "react-icons/fi";
import SupportSEO from "../../components/Support/SupportSEO";
import SupportBreadcrumb from "../../components/Support/SupportBreadcrumb";
import { SUPPORT_EMAIL } from "../../components/SEO/siteOrigin";
import contentManagerV3 from "../../utils/contentManagerV3";
import { generateSupportBreadcrumbs } from "../../utils/supportBreadcrumbs";

const compactLink =
  "inline-flex items-center justify-center px-3 py-2 text-sm text-brand-charcoal bg-brand-cream font-display font-bold tracking-wide border border-brand-charcoal/20 rounded-lg hover:border-brand-gold-ink";

const Support = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [filteredContent, setFilteredContent] = useState([]);

  // Icon mapping function
  const getIconComponent = (iconName) => {
    const iconMap = {
      FiBook: FiBook,
      FiVideo: FiVideo,
      FiUsers: FiUsers,
      FiDollarSign: FiDollarSign,
      FiTool: FiTool,
      FiHelpCircle: FiHelpCircle,
    };
    return iconMap[iconName] || FiFileText;
  };

  // Get content from content manager (memoized to prevent infinite loops)
  const supportContent = useMemo(() => {
    return contentManagerV3
      .getCategories()
      .filter((category) => !category.hidden) // Filter out hidden categories
      .map((category) => ({
        ...category,
        icon: getIconComponent(category.icon),
        articles: contentManagerV3.getArticlesByCategory(category.id),
      }));
  }, []);

  // Filter content based on search and category
  useEffect(() => {
    let filtered = supportContent;

    // Filter by category
    if (activeCategory !== "all") {
      filtered = filtered.filter((item) => item.id === activeCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const searchResults = contentManagerV3.searchArticles(searchQuery);
      const searchCategoryIds = new Set(
        searchResults.map((article) => article.category)
      );

      filtered = filtered
        .map((category) => ({
          ...category,
          articles: category.articles.filter((article) =>
            searchResults.some((result) => result.id === article.id)
          ),
        }))
        .filter((category) => category.articles.length > 0);
    }

    setFilteredContent(filtered);
  }, [searchQuery, activeCategory, supportContent]);

  const categories = [
    {
      id: "all",
      label: "All Content",
      count: supportContent.reduce((acc, cat) => acc + cat.articles.length, 0),
    },
    ...supportContent.map((category) => ({
      id: category.id,
      label: category.title,
      count: category.articles.length,
    })),
  ];

  // Generate breadcrumbs
  const breadcrumbs = generateSupportBreadcrumbs("/support");

  return (
    <>
      <SupportSEO
        title="Bowling Tournament Support - Help Center & FAQs"
        description="Get help with bowling tournament management. Find tutorials, FAQs, and support for creating tournaments, scoring, payouts, and brackets."
        keywords={[
          "bowling tournament support",
          "help center",
          "tournament management",
          "bowling tutorials",
          "tournament FAQ",
          "bowling brackets",
        ]}
        canonicalUrl="/support"
      />
      <div className="min-h-screen bg-brand-cream py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex justify-center mb-4">
            <SupportBreadcrumb items={breadcrumbs} />
          </div>

          <div className="card-board overflow-hidden mb-8">
            <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
              <h1 className="font-display font-bold text-xl md:text-2xl text-brand-cream tracking-tight">
                Help
              </h1>
              <p className="text-sm text-brand-mist mt-1">
                Guides for setting up and running a tournament.
              </p>
            </div>
            <div className="p-6">
              <div className="relative max-w-2xl">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-brand-muted text-xl" />
                <input
                  type="text"
                  placeholder="Search articles, tutorials, or FAQs"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => setActiveCategory(category.id)}
                className={`px-3 py-2 rounded-lg text-sm font-display font-bold tracking-wide transition-colors ${
                  activeCategory === category.id
                    ? "bg-brand-charcoal text-brand-cream"
                    : "bg-brand-cream text-brand-charcoal border border-brand-charcoal/20 hover:border-brand-charcoal"
                }`}
              >
                {category.label}
                <span className="ml-2 text-sm font-normal opacity-75">
                  ({category.count})
                </span>
              </button>
            ))}
          </div>

          <div className="space-y-8">
            {filteredContent.map((category) => (
              <section key={category.id} className="card-board p-6 md:p-8">
                <div className="flex items-center mb-6">
                  <category.icon className="text-2xl text-brand-muted mr-3 flex-shrink-0" />
                  <div>
                    <h2 className="heading-display text-xl">
                      {category.title}
                    </h2>
                    <p className="text-brand-muted">{category.description}</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {category.articles.map((article) => (
                    <Link
                      key={article.id}
                      to={article.path}
                      className="group block p-5 card-inset hover:border-brand-charcoal transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3 gap-2">
                        <h3 className="font-display font-bold text-brand-charcoal group-hover:underline">
                          {article.title}
                        </h3>
                        <FiArrowRight className="text-brand-muted flex-shrink-0 mt-1" />
                      </div>
                      <p className="text-brand-muted text-sm mb-4 line-clamp-2">
                        {article.description}
                      </p>
                      <div className="flex items-center justify-end">
                        <span className="text-xs text-brand-mist">
                          {article.estimatedTime}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <div className="mt-12 card-board p-8 text-center">
            <h3 className="heading-display text-xl mb-2">Still stuck?</h3>
            <p className="text-brand-muted mb-6">
              Can't find it here. Send a ticket and we'll take a look.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className={compactLink}
              >
                Email Support
              </a>
              <Link to="/support/contact" className={compactLink}>
                Submit a Ticket
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Support;
